<template>
  <div class="billing-tab">

    <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null" class="mb-3">
      {{ error }}
    </b-alert>

    <!-- Disable billing modal -->
    <b-modal
      v-model="showDisableModal"
      centered
      no-close-on-backdrop
      hide-header
      hide-footer
      size="lg"
      @hide="onDisableModalHide"
    >
      <div class="dm-root">
        <button class="dm-close" @click="onDisableModalHide" aria-label="Close">
          <b-icon icon="x" />
        </button>
        <div class="dm-header">
          <div class="dm-header__icon-wrap">
            <b-icon icon="toggle-off" class="dm-header__icon" />
          </div>
          <div>
            <p class="dm-header__title">Disable Billing</p>
            <p class="dm-header__sub" v-if="disablePendingContext && disablePendingContext.type === 'bulk'">
              Turning off billing for
              <strong>{{ disablePendingContext.count }} TPA{{ disablePendingContext.count !== 1 ? 's' : '' }}</strong>
            </p>
            <p class="dm-header__sub" v-else-if="disablePendingContext">
              Turning off billing for
              <strong>{{ disablePendingContext.tpa && disablePendingContext.tpa.name }}</strong>
            </p>
          </div>
        </div>
        <div class="dm-info">
          <p class="dm-info__label">
            <b-icon icon="info-circle-fill" class="mr-1" /> What this means
          </p>
          <div class="dm-info__row">
            <span class="dm-info__check">&#10003;</span>
            <span>Future new BDX keys will <strong>not</strong> have billing automatically enabled.</span>
          </div>
          <div class="dm-info__row">
            <span class="dm-info__check">&#10003;</span>
            <span>Existing keys will remain billable unless manually switched off at the key level.</span>
          </div>
          <div class="dm-info__row">
            <span class="dm-info__check">&#10003;</span>
            <span>Existing and pending invoice charges are <strong>not affected</strong> and will continue through the normal billing process.</span>
          </div>
        </div>
        <div class="dm-actions">
          <b-button variant="secondary" @click="onDisableModalHide">Keep Enabled</b-button>
          <b-button variant="danger" @click="confirmDisable">Disable</b-button>
        </div>
      </div>
    </b-modal>

    <!-- Add TPA billing modal -->
    <BillingAddTpaModal
      v-if="addTpaContext"
      v-model="showAddTpaModal"
      :underwriter-name="addTpaContext ? addTpaContext.uw.underwriter_name : ''"
      :available-tpas="addTpaContext ? addTpaContext.uw.available_tpas : []"
      @add="onAddTpaConfirm"
    />

    <!-- Toolbar -->
    <div class="billing-toolbar">
      <div class="d-flex align-items-center gap-2">
        <b-form-select v-model="selectedBillingState" size="sm" class="billing-select">
          <b-form-select-option :value="null">All billing states</b-form-select-option>
          <b-form-select-option value="enabled">Billing enabled</b-form-select-option>
          <b-form-select-option value="disabled">Billing disabled</b-form-select-option>
        </b-form-select>
        <div class="navbar-search form-inline">
          <b-form-group class="mb-0">
            <b-input-group class="input-group-alternative input-group-merge">
              <div class="input-group-prepend">
                <span class="input-group-text"><i class="fas fa-search"></i></span>
              </div>
              <b-form-input
                v-model="globalSearch"
                placeholder="Search underwriter..."
                size="sm"
                type="text"
              />
            </b-input-group>
          </b-form-group>
        </div>
      </div>
      <b-button variant="outline-secondary" size="sm" @click="loadBillingData" :disabled="loading" class="refresh-btn">
        <b-icon icon="arrow-clockwise" :animation="loading ? 'spin' : ''" class="mr-1" />
        Refresh
      </b-button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5 text-muted small">
      Loading billing configuration...
    </div>

    <template v-if="!loading">

      <!-- Summary -->
      <div v-if="filteredUnderwriters.length > 0" class="billing-summary-bar">
        <span class="font-weight-bold">{{ filteredUnderwriters.length }} underwriter{{ filteredUnderwriters.length !== 1 ? 's' : '' }}</span>
        <span class="text-muted small ml-2">· {{ totalTPAs }} TPA{{ totalTPAs !== 1 ? 's' : '' }}</span>

      </div>

      <!-- Group header row: "Billing Type" spans the Market/LIMOSS columns.
           Shares its grid-template-columns with .uw-header--bt below, which is
           what makes the label sit over the controls it describes. -->
      <div v-if="canEditBillingType && filteredUnderwriters.length > 0" class="bt-group-header uw-header--bt">
        <span></span>
        <span class="bt-group-header__label">
          Billing Type
          <b-icon id="bt-help" icon="info-circle-fill" class="bt-header__info" />
          <b-tooltip target="bt-help" placement="bottomleft" triggers="hover focus">
            <div class="bt-tip">
              <p v-for="s in billingTypeScenarios" :key="s.title">
                <strong>{{ s.title }}</strong> — {{ s.body }}
              </p>
            </div>
          </b-tooltip>
        </span>
        <span></span>
        <span></span>
      </div>

      <!-- Column labels for the billing-type switches. Shares its
           grid-template-columns with .uw-header--bt below, which is what makes
           the labels sit over the controls they describe. -->
      <div v-if="canEditBillingType && filteredUnderwriters.length > 0" class="bt-header uw-header--bt">
        <span class="bt-header__title">
          Underwriter
        </span>
        <span v-for="scheme in billingTypeSchemes" :key="scheme" class="bt-header__col">
          {{ schemeLabels[scheme] }}
        </span>
        <span></span>
        <span></span>
      </div>

      <!-- Accordion -->
      <div class="billing-accordion" v-if="filteredUnderwriters.length > 0">
        <div
          v-for="uw in filteredUnderwriters"
          :key="uw.underwriter_id"
          class="uw-card"
        >
          <!-- Header -->
          <div
            class="uw-header"
            :class="{
              'uw-header--open': openAccordions.includes(uw.underwriter_id),
              'uw-header--bt': canEditBillingType
            }"
            @click="toggleAccordion(uw.underwriter_id)"
          >
            <!-- Grid column 1. Name and both badges live together so column 1 is
                 the only flexible one - every other column is a fixed width, which
                 is what lets the label row above line up with these rows. Two
                 separate grids do not share `auto` column widths, so nothing here
                 may be auto-sized. -->
            <div class="uw-header__main">
              <div class="uw-header__left">
                <b-icon icon="building" class="uw-header__icon" />
                <strong class="uw-header__name">{{ uw.underwriter_name }}</strong>
                <span class="tpa-count-badge">{{ uw._allTpas.length }} TPA{{ uw._allTpas.length !== 1 ? 's' : '' }}</span>
              </div>
              <span class="billing-status-badge" :class="'billing-status-badge--' + getBillingBadgeVariant(uw)">
                {{ getBillingStatus(uw) }}
              </span>
            </div>

            <!-- One grid column per scheme. @click.stop because the whole header
                 row is the accordion toggle. -->
            <template v-if="canEditBillingType">
                <div
                  v-for="scheme in billingTypeSchemes"
                  :key="scheme"
                  class="bt-cell"
                  @click.stop
                >
                  <!-- 52px slot. base-switch renders .custom-toggle, which the
                       theme sets to width:100% capped at $custom-toggle-width - so
                       it needs a definite parent. Dropped straight into a flex row
                       it resolves against the whole cell and then gets squeezed by
                       the date picker, and the slider (position:absolute, inset 0)
                       stretches into a long pill. -->
                  <!-- The lock lives on this wrapper, NOT as :disabled on the
                       switch: BaseSwitch declares no disabled prop, so the binding
                       would land on its root <label> and the checkbox inside would
                       stay clickable. setBillingTypeEnabled guards it as well,
                       because pointer-events cannot stop a keyboard Space on a
                       focused checkbox. -->
                  <span
                    class="bt-toggle"
                    :class="{ 'bt-toggle--locked': !billingTypeEditable(uw) || !!uw.billing_type._saving }"
                  >
                    <base-switch
                      :key="scheme + '-' + uw.billing_type._switchKey"
                      :id="'bt-switch-' + uw.underwriter_id + '-' + scheme"
                      :value="uw.billing_type[scheme + '_enabled']"
                      onText=""
                      offText=""
                      @input="val => setBillingTypeEnabled(uw, scheme, val)"
                    />
                  </span>
                  <DatePicker
                    :value="uw.billing_type[scheme + '_start_date']"
                    type="date"
                    format="DD MMM YYYY"
                    value-type="YYYY-MM-DD"
                    :placeholder="uw.billing_type[scheme + '_enabled'] ? 'Select date' : '—'"
                    :disabled-date="disabledBeforeTomorrow"
                    :disabled="!billingTypeEditable(uw) || !!uw.billing_type._saving"
                    :clearable="false"
                    class="billing-date-picker"
                    @input="val => setBillingTypeDate(uw, scheme, val)"
                  />
                </div>

                <!-- Edit / Save / Cancel, matching the per-TPA rows below. Nothing
                     is written until Save, so one deliberate change is one history
                     row rather than one per click. An underwriter with neither
                     scheme committed is directly editable - only live config needs
                     the extra Edit click. -->
                <div class="bt-actions" @click.stop>
                  <template v-if="!billingTypeEditable(uw)">
                    <b-button size="sm" variant="outline-secondary"
                              @click="startBillingTypeEdit(uw)">Edit</b-button>
                  </template>
                  <template v-else>
                    <b-button size="sm" variant="primary"
                              :disabled="!!uw.billing_type._saving"
                              @click="saveBillingTypeEdit(uw)">
                      {{ uw.billing_type._saving ? 'Saving…' : 'Save' }}
                    </b-button>
                    <b-button size="sm" variant="link" class="text-muted px-1"
                              :disabled="!!uw.billing_type._saving"
                              @click="cancelBillingTypeEdit(uw)">Cancel</b-button>
                  </template>
                </div>
            </template>

            <b-icon :icon="openAccordions.includes(uw.underwriter_id) ? 'chevron-up' : 'chevron-down'" class="uw-header__chevron" />
          </div>

          <!-- Body -->
          <div v-if="openAccordions.includes(uw.underwriter_id)" class="uw-body">

            <!-- Bulk bar -->
            <div v-if="uwSelectedIds(uw).length > 0" class="bulk-bar mx-3 mt-2 mb-2 px-3 py-2 d-flex align-items-center flex-wrap">
              <span class="bulk-count mr-3">{{ uwSelectedIds(uw).length }} selected</span>
              <div class="d-flex align-items-center mr-3">
                <label class="bulk-label mr-2 mb-0">Start Date</label>
                <DatePicker
                  :value="uw._bulkMonth"
                  type="date"
                  format="DD MMM YYYY"
                  value-type="YYYY-MM-DD"
                  placeholder="Select date"
                  :disabled-date="disabledBeforeToday"
                  :clearable="false"
                  class="billing-date-picker"
                  @input="val => onBulkMonthChange(uw, val)"
                />
              </div>
              <b-button size="sm" variant="primary" :disabled="uw._bulkSaving" class="mr-2" @click="bulkEnable(uw)">
                {{ uw._bulkSaving ? 'Saving…' : 'Enable' }}
              </b-button>
              <b-button size="sm" variant="outline-danger" :disabled="uw._bulkSaving" @click="bulkDisable(uw)">
                {{ uw._bulkSaving ? 'Saving…' : 'Disable' }}
              </b-button>
            </div>

            <!-- Per-accordion TPA search -->
            <div class="tpa-search-bar px-3 pt-2 pb-1 d-flex align-items-center justify-content-between">
              <b-form-input
                :value="uw._tpaSearch" @input="val => { uw._tpaSearch = val; onTpaSearch(uw) }"
                placeholder="Search TPAs..."
                size="sm"
                class="tpa-search-input"
              />
              <b-button
                variant="link"
                size="sm"
                class="add-tpa-btn ml-2 px-0 flex-shrink-0"
                @click.stop="openAddTpaModal(uw)"
              >
                <b-icon icon="plus-circle" class="mr-1" />
                Add TPA
              </b-button>
            </div>

            <!-- Empty -->
            <div v-if="uw.tpas.length === 0" class="text-center text-muted py-3 small">
              No TPAs associated with this underwriter.
              <a href class="d-block mt-1 text-info small" @click.prevent="openAddTpaModal(uw)">Add one now</a>
            </div>

            <!-- el-table -->
            <div v-else class="uw-table-wrap">
              <el-table
                :ref="'table-' + uw.underwriter_id"
                :data="uw.tpas"
                row-key="id"
                empty-text="No TPAs match your search."
                header-row-class-name="thead-light"
                :row-class-name="({ row }) => getRowClass(row)"
                @sort-change="({ prop, order }) => onSortChange(uw, prop, order)"
                @selection-change="(sel) => onSelectionChange(uw, sel)"
              >
                <el-table-column type="selection" width="40" />

                <el-table-column prop="name" label="TPA" min-width="180" sortable="custom" :sort-orders="['ascending', 'descending']">
                  <template slot-scope="scope">
                    <span :class="{ 'tpa-off': scope.row.billing_status === 0 }">{{ scope.row.name }}</span>
                  </template>
                </el-table-column>

                <el-table-column prop="billing_status" label="Billing" width="100" sortable="custom" :sort-orders="['ascending', 'descending']">
                  <template slot-scope="scope">
                    <base-switch
                      :key="scope.row._switchKey"
                      :id="'billing-switch-' + uw.underwriter_id + '-' + scope.row.id"
                      :value="scope.row.billing_status !== 0"
                      onText=""
                      offText=""
                      :disabled="scope.row.saving"
                      @input="val => toggleBilling(uw, scope.row, val)"
                    />
                  </template>
                </el-table-column>

                <el-table-column prop="billing_start_period" label="Start Submission Date" min-width="210" sortable="custom" :sort-orders="['ascending', 'descending']">
                  <template slot-scope="scope">
                    <DatePicker
                      v-if="scope.row.billing_status !== 0"
                      :value="scope.row.billing_start_period"
                      type="date"
                      format="DD MMM YYYY"
                      value-type="YYYY-MM-DD"
                      placeholder="Select date"
                      :disabled-date="disabledBeforeToday"
                      :disabled="!isEditable(scope.row) || scope.row.saving"
                      :clearable="false"
                      class="billing-date-picker"
                      @input="val => onMonthPickerChange(scope.row, val)"
                    />
                    <span v-else class="placeholder-date">—</span>
                  </template>
                </el-table-column>

                <el-table-column label="Actions" width="140">
                  <template slot-scope="scope">
                    <div class="row-actions">
                      <template v-if="scope.row._committed_status !== 0 && !scope.row.editing">
                        <b-button size="sm" variant="outline-secondary" @click="startEdit(scope.row)">Edit</b-button>
                      </template>
                      <template v-else>
                        <b-button size="sm" variant="primary" :disabled="scope.row.saving" @click="saveEdit(uw, scope.row)">
                          {{ scope.row.saving ? 'Saving…' : 'Save' }}
                        </b-button>
                        <b-button
                          v-if="scope.row.editing"
                          size="sm"
                          variant="link"
                          class="text-muted px-1"
                          :disabled="scope.row.saving"
                          @click="cancelEdit(scope.row)"
                        >Cancel</b-button>
                      </template>
                    </div>
                  </template>
                </el-table-column>
              </el-table>

              <!-- Row count -->
              <div class="uw-footer px-3 py-1">
                <span class="text-muted small">
                  {{ uw.tpas.length }} of {{ uw._allTpas.length }} TPA{{ uw._allTpas.length !== 1 ? 's' : '' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="empty-state">
        <b-icon icon="inbox" font-scale="2" class="text-muted mb-2" />
        <p class="text-muted small mb-0">No billing data available.</p>
      </div>

    </template>
  </div>
</template>

<script>
import { BillingManagementService } from '@services/BillingManagementService'
import { Select, Option, Table, TableColumn } from 'element-ui'
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'
import BillingAddTpaModal from '@components/billing/BillingAddTPAMoal'
import {
  SCHEME_MARKET,
  SCHEME_LIMOSS,
  SCHEME_LABELS,
  BILLING_TYPE_SCENARIOS,
  isBeforeEarliestStart,
} from '@constants/billing'
import Vue from 'vue'

export default {
  name: 'BillingStatusTab',
  components: {
    DatePicker,
    BillingAddTpaModal,
    [Select.name]: Select,
    [Option.name]: Option,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
  },
  props: {
    viewMode: { type: String, default: 'superuser' }
  },
  data() {
    return {
      underwriters: [],
      loading: false,
      error: null,
      openAccordions: [],
      selectedBillingState: 'enabled',
      globalSearch: '',
      showDisableModal: false,
      disablePendingContext: null,
      showAddTpaModal: false,
      addTpaContext: null,
    }
  },
  computed: {
    /** Market first, then LIMOSS - the order the switches read on screen. */
    billingTypeSchemes() {
      return [SCHEME_MARKET, SCHEME_LIMOSS]
    },
    schemeLabels() {
      return SCHEME_LABELS
    },
    billingTypeScenarios() {
      return BILLING_TYPE_SCENARIOS
    },
    /**
     * Billing type decides how an underwriter is charged, so only superusers set
     * it. The server enforces this independently - hiding a control is not a
     * permission - this just keeps it off screen for everyone else.
     */
    canEditBillingType() {
      // The viewMode prop is never bound by any caller, so it always fell back to
      // its default and this gate was permanently true. Read the real identity
      // instead, the way the other eight $user.get().is_superuser checks in this
      // app do. The server check in CompanyBillingTypeUpdateView remains the
      // authority either way - hiding a control is not a permission.
      return !!(this.$user && this.$user.get() && this.$user.get().is_superuser)
    },

    filteredUnderwriters() {
      let result = this.underwriters
      const q = this.globalSearch ? this.globalSearch.toLowerCase().trim() : ''
      if (q) {
        result = result.filter(function(uw) {
          return uw.underwriter_name.toLowerCase().includes(q)
        })
      }
      if (this.selectedBillingState === 'enabled') {
        result = result.filter(function(uw) {
          return uw._allTpas.some(function(t) { return t.billing_status })
        })
      } else if (this.selectedBillingState === 'disabled') {
        result = result.filter(function(uw) {
          return uw._allTpas.every(function(t) { return !t.billing_status })
        })
      }
      return result
    },
    totalTPAs() {
      return this.filteredUnderwriters.reduce(function(sum, uw) {
        return sum + uw._allTpas.length
      }, 0)
    },
  },
  async created() {
    await this.loadBillingData()
  },
  methods: {
    /**
     * A start date must be tomorrow or later: the daily activation job needs a
     * run before the date arrives, so a same-day start is never picked up.
     */
    disabledBeforeTomorrow(d) {
      return isBeforeEarliestStart(d)
    },

    /**
     * 'YYYY-MM-DD' -> a local Date. Built from parts rather than handed to the
     * Date parser, which reads a bare date string as UTC midnight - west of
     * Greenwich that is the previous day, so a perfectly valid start of tomorrow
     * would be judged already past.
     */
    parseStartDate(value) {
      var parts = String(value).split('-')
      return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]))
    },

    /** Nothing is committed until Save, so these only touch local state. */
    setBillingTypeEnabled(uw, scheme, enabled) {
      // Reject and remount when the row is not in edit mode. The remount is what
      // undoes the flip - the browser has already changed the checkbox natively,
      // and rewriting the prop to the value it already holds is not a change Vue
      // can see.
      if (!this.billingTypeEditable(uw) || uw.billing_type._saving) {
        Vue.set(uw.billing_type, '_switchKey', (uw.billing_type._switchKey || 0) + 1)
        return
      }
      Vue.set(uw.billing_type, scheme + '_enabled', enabled)
    },

    setBillingTypeDate(uw, scheme, value) {
      if (!value) return
      if (!this.billingTypeEditable(uw) || uw.billing_type._saving) return
      Vue.set(uw.billing_type, scheme + '_start_date', value)
      // Picking a date implies the scheme is meant to be on.
      Vue.set(uw.billing_type, scheme + '_enabled', true)
    },

    /**
     * Editable only while actually editing. The per-TPA rows additionally treat an
     * un-configured row as editable, but that reads wrong here: every underwriter
     * is one row in one list, so a row showing Save while its neighbour shows Edit
     * looks broken - and on an untouched row Save has nothing to send anyway.
     */
    billingTypeEditable(uw) {
      // uw.billing_type is always set by loadBillingData, but this runs on every
      // row render and a throw here surfaces as a blank table, not a stack trace.
      return !!(uw.billing_type && uw.billing_type._editing)
    },

    startBillingTypeEdit(uw) {
      Vue.set(uw.billing_type, '_editing', true)
    },

    cancelBillingTypeEdit(uw) {
      var bt = uw.billing_type
      Vue.set(uw, 'billing_type', Object.assign({}, bt, bt._committed, {
        _editing: false,
        _saving: null,
        // The browser flips the checkbox natively, so restoring the prop is not a
        // change Vue can see. Remounting the switch is what actually reverts it.
        _switchKey: (bt._switchKey || 0) + 1,
      }))
    },

    /**
     * Validate both schemes, then save only the ones that actually changed.
     *
     * One request per scheme, sequentially - the endpoint takes one scheme per
     * call precisely so a failure on one cannot half-apply the other. Each
     * response is authoritative and is applied as it arrives, so a failure on the
     * second leaves the first genuinely saved and the screen showing the truth.
     */
    async saveBillingTypeEdit(uw) {
      var bt = uw.billing_type
      if (bt._saving) return

      for (var i = 0; i < this.billingTypeSchemes.length; i++) {
        var sc = this.billingTypeSchemes[i]
        if (!bt[sc + '_enabled']) continue
        var d = bt[sc + '_start_date']
        if (!d) {
          Vue.prototype.$toasted.error(
            'Pick a start date for ' + SCHEME_LABELS[sc] + ' before saving.')
          return
        }
        // A disabled scheme deliberately keeps its date, so re-enabling can carry
        // one that has since passed. The server would reject it; say so here.
        if (isBeforeEarliestStart(this.parseStartDate(d))) {
          Vue.prototype.$toasted.error(
            'The start date for ' + SCHEME_LABELS[sc] +
            ' has passed. Pick a new one before saving.')
          return
        }
      }

      var changed = this.billingTypeSchemes.filter(function (sc) {
        var wasOn = bt._committed[sc + '_enabled']
        var isOn = bt[sc + '_enabled']
        if (wasOn !== isOn) return true
        return isOn && bt[sc + '_start_date'] !== bt._committed[sc + '_start_date']
      })

      if (!changed.length) {
        Vue.set(uw.billing_type, '_editing', false)
        return
      }

      // Snapshot what the user asked for BEFORE any request goes out. Each save
      // returns the whole config, and the answer to the first one cannot yet
      // include the scheme still queued - so reading the live object mid-loop
      // would send that scheme back as false and silently drop it. Turning both
      // switches on in one edit saved only the first before this.
      var intent = {}
      for (var i = 0; i < changed.length; i++) {
        var sc = changed[i]
        intent[sc] = {
          enabled: bt[sc + '_enabled'],
          startDate: bt[sc + '_enabled'] ? bt[sc + '_start_date'] : null,
        }
      }

      var lastRes = null
      for (var j = 0; j < changed.length; j++) {
        var scheme = changed[j]
        Vue.set(uw.billing_type, '_saving', scheme)
        try {
          lastRes = await BillingManagementService.updateBillingType(uw.underwriter_id, {
            scheme: scheme,
            enabled: intent[scheme].enabled,
            startDate: intent[scheme].startDate,
          })
        } catch (err) {
          var detail = err && err.response && err.response.data && err.response.data.error
          Vue.prototype.$toasted.error(
            (detail || 'Could not update billing type') +
            ' (' + SCHEME_LABELS[scheme] + ')')
          // Stay in edit mode so it can be corrected and retried, showing what the
          // server actually holds. An earlier scheme in this same edit may already
          // have saved, so prefer that response over _committed, which predates it.
          if (lastRes) {
            this.applyBillingTypeResponse(uw, lastRes)
            Vue.set(uw.billing_type, '_editing', true)
          } else {
            Vue.set(uw, 'billing_type', Object.assign({}, uw.billing_type,
              uw.billing_type._committed, {
                _editing: true,
                _saving: null,
                _switchKey: (uw.billing_type._switchKey || 0) + 1,
              }))
          }
          return
        }
      }

      // Applied once, after every scheme is stored, so the block is rebuilt from a
      // response that reflects all of them.
      this.applyBillingTypeResponse(uw, lastRes)
      Vue.set(uw.billing_type, '_editing', false)
      Vue.prototype.$toasted.show('Billing type updated for ' + uw.underwriter_name + '.')
    },

    /** The response is the truth; rebuild the block from it. */
    applyBillingTypeResponse(uw, res) {
      // APIRenderer wraps every response as {status, data}, so the payload is
      // one level deeper than res.data. Reading it too shallow yielded undefined,
      // which reset every field to false and wiped the scheme still to be saved.
      var body = (res && res.data) || {}
      var payload = (body && typeof body === 'object'
        && 'status' in body && 'data' in body) ? body.data : body
      var b = (payload && payload.billing_type) || {}
      var committed = {
        market_enabled: !!b.market_enabled,
        market_start_date: b.market_start_date || null,
        limoss_enabled: !!b.limoss_enabled,
        limoss_start_date: b.limoss_start_date || null,
      }
      Vue.set(uw, 'billing_type', Object.assign({}, committed, {
        _committed: committed,
        _editing: uw.billing_type._editing,
        _saving: null,
        _switchKey: (uw.billing_type._switchKey || 0) + 1,
      }))
    },



    // ── Date helpers ──────────────────────────────────────

    _todayIso() {
      var now = new Date()
      return now.getFullYear() + '-'
        + String(now.getMonth() + 1).padStart(2, '0') + '-'
        + String(now.getDate()).padStart(2, '0')
    },

    disabledBeforeToday(date) {
      var now = new Date()
      var today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      return date < today
    },

    onMonthPickerChange(tpa, val) {
      if (!val) {
        Vue.set(tpa, 'billing_start_period', null)
        return
      }
      Vue.set(tpa, 'billing_start_period', val)
      if (!tpa.editing && tpa._committed_status !== 0) {
        Vue.set(tpa, 'editing', true)
      }
    },

    onBulkMonthChange(uw, val) {
      uw._bulkMonth = val || null
    },

    // ── Data loading ──────────────────────────────────────

    async loadBillingData() {
      this.loading = true
      this.error = null
      try {
        var response = await BillingManagementService.getAllBillingList()
        var raw = (response.data && response.data.data && response.data.data.data) || []
        var list = Array.isArray(raw) ? raw : []

        this.underwriters = list.map(function(uw) {
          var tpas = (uw.tpas || []).map(function(tpa) {
            var billing_status = tpa.billing_status ? 1 : 0
            var billing_start_period = null
            if (tpa.billing_start_period) {
              billing_start_period = tpa.billing_start_period.slice(0, 10)
            } else if (tpa.billing_month && tpa.billing_year) {
              billing_start_period = tpa.billing_year + '-' + String(tpa.billing_month).padStart(2, '0') + '-01'
            }
            return {
              id: tpa.tpa_company_id,
              name: tpa.tpa_company_name,
              billing_status: billing_status,
              billing_start_period: billing_start_period,
              _committed_status: billing_status,
              editing: false,
              saving: false,
              _original: null,
              _switchKey: 0,
            }
          })

          // available_tpas from API — TPAs not yet configured for this underwriter
          var available_tpas = (uw.available_tpas || []).map(function(t) {
            return { id: t.id, name: t.name }
          })

          // Underwriter-level billing type. Absent or unset serialises as both
          // switches off, so no null handling is needed downstream.
          var bt = uw.billing_type || {}

          return {
            underwriter_id: uw.underwriter_id,
            underwriter_name: uw.underwriter_name,
            underwriter_rls_id: uw.underwriter_rls_id,
            tpas: tpas,
            _allTpas: tpas.slice(),
            available_tpas: available_tpas,
            billing_type: {
              market_enabled: !!bt.market_enabled,
              market_start_date: bt.market_start_date || null,
              limoss_enabled: !!bt.limoss_enabled,
              limoss_start_date: bt.limoss_start_date || null,
              // What the server currently holds. Edits are made against the flat
              // fields above; Cancel restores from here, and Save diffs against it
              // so only genuinely changed schemes are sent.
              _committed: {
                market_enabled: !!bt.market_enabled,
                market_start_date: bt.market_start_date || null,
                limoss_enabled: !!bt.limoss_enabled,
                limoss_start_date: bt.limoss_start_date || null,
              },
              _editing: false,
              _saving: null,
              // Bumped to force the switch to remount. The browser flips the
              // checkbox natively, so restoring the prop after a Cancel or a
              // rejected save is not a change Vue can see on its own.
              _switchKey: 0,
            },
            _sortField: null,
            _sortOrder: null,
            _selectedIds: [],
            _bulkMonth: null,
            _bulkSaving: false,
            _tpaSearch: '',
          }
        })
      } catch (err) {
        console.error('Failed to load billing data:', err)
        this.error = 'Failed to load billing data. Please try again.'
      } finally {
        this.loading = false
      }
    },

    toggleAccordion(id) {
      var idx = this.openAccordions.indexOf(id)
      if (idx === -1) {
        this.openAccordions.push(id)
      } else {
        this.openAccordions.splice(idx, 1)
      }
    },

    // ── Table helpers ─────────────────────────────────────

    applySearchAndSort(uw) {
      var q = uw._tpaSearch ? uw._tpaSearch.toLowerCase().trim() : ''
      var list = uw._allTpas.filter(function(t) {
        return !q || t.name.toLowerCase().includes(q)
      })
      if (uw._sortField) {
        var field = uw._sortField
        var asc = uw._sortOrder === 'ascending'
        list = list.slice().sort(function(a, b) {
          var av = a[field] == null ? '' : a[field]
          var bv = b[field] == null ? '' : b[field]
          if (av < bv) return asc ? -1 : 1
          if (av > bv) return asc ? 1 : -1
          return 0
        })
      }
      uw.tpas.splice(0, uw.tpas.length)
      for (var i = 0; i < list.length; i++) {
        uw.tpas.push(list[i])
      }
    },

    uwSelectedIds(uw) {
      return uw._selectedIds || []
    },

    onSelectionChange(uw, selection) {
      uw._selectedIds = selection.map(function(r) { return r.id })
    },

    onSortChange(uw, prop, order) {
      uw._sortField = prop
      uw._sortOrder = order
      this.applySearchAndSort(uw)
    },

    onTpaSearch(uw) {
      this.applySearchAndSort(uw)
    },

    getRowClass(row) {
      if (row.saving) return 'row-saving'
      if (this.isEditable(row)) return 'row-editing'
      return ''
    },

    // ── TPA row operations ────────────────────────────────

    isEditable(tpa) {
      return tpa._committed_status === 0 || tpa.editing
    },

    startEdit(tpa) {
      tpa._original = {
        billing_status: tpa.billing_status,
        billing_start_period: tpa.billing_start_period,
      }
      Vue.set(tpa, 'editing', true)
    },

    cancelEdit(tpa) {
      if (tpa._original) {
        Vue.set(tpa, 'billing_status', tpa._original.billing_status)
        Vue.set(tpa, 'billing_start_period', tpa._original.billing_start_period)
      }
      Vue.set(tpa, 'editing', false)
      tpa._original = null
    },

    async saveEdit(uw, tpa) {
      if (tpa.billing_status !== 0 && !tpa.billing_start_period) {
        this.error = 'Please set a Billing Date before enabling billing for ' + tpa.name + '.'
        return
      }
      Vue.set(tpa, 'saving', true)
      try {
        await BillingManagementService.updateBilling(uw.underwriter_id, {
          tpaCompanyId: tpa.id,
          billingStatus: tpa.billing_status,
          billingStartPeriod: tpa.billing_start_period,
        })
        Vue.set(tpa, '_committed_status', tpa.billing_status)
        Vue.set(tpa, 'editing', false)
        tpa._original = null
        if (Vue.prototype.$toasted) {
          Vue.prototype.$toasted.show('Billing updated successfully', { type: 'success', duration: 3000 })
        }
      } catch (err) {
        console.error('Failed to update billing:', err)
        this.error = 'Failed to update billing for ' + tpa.name + '. Please try again.'
      } finally {
        Vue.set(tpa, 'saving', false)
      }
    },

    toggleBilling(uw, tpa, enabled) {
      if (!enabled && tpa._committed_status !== 0) {
        this.disablePendingContext = { type: 'single', uw: uw, tpa: tpa }
        this.showDisableModal = true
        return
      }
      Vue.set(tpa, 'editing', true)
      Vue.set(tpa, 'billing_status', enabled ? 1 : 0)
      Vue.set(tpa, 'billing_start_period', enabled ? this._todayIso() : null)
    },

    confirmDisable() {
      var ctx = this.disablePendingContext
      this.disablePendingContext = null
      this.showDisableModal = false
      if (!ctx) return
      if (ctx.type === 'single') {
        Vue.set(ctx.tpa, 'billing_status', 0)
        Vue.set(ctx.tpa, 'billing_start_period', null)
        Vue.set(ctx.tpa, 'editing', true)
        this.saveEdit(ctx.uw, ctx.tpa)
      } else if (ctx.type === 'bulk') {
        this._executeBulkDisable(ctx.uw, ctx.ids)
      }
    },

    onDisableModalHide() {
      var ctx = this.disablePendingContext
      this.disablePendingContext = null
      this.showDisableModal = false
      if (ctx && ctx.type === 'single') {
        Vue.set(ctx.tpa, '_switchKey', ctx.tpa._switchKey + 1)
      }
    },

    // ── Bulk operations ───────────────────────────────────

    async bulkEnable(uw) {
      if (!uw._bulkMonth) {
        this.error = 'Please select a Billing Date before enabling billing.'
        return
      }
      var offIds = uw.tpas
        .filter(function(t) { return uw._selectedIds.includes(t.id) && t._committed_status === 0 })
        .map(function(t) { return t.id })
      if (offIds.length === 0) return

      Vue.set(uw, '_bulkSaving', true)
      try {
        await BillingManagementService.bulkUpdateBilling(uw.underwriter_id, {
          enable: true,
          billingStartPeriod: uw._bulkMonth,
          updates: offIds.map(function(id) { return { tpa_company_id: id } }),
        })
        uw.tpas.forEach(function(tpa) {
          if (offIds.includes(tpa.id)) {
            Vue.set(tpa, 'billing_status', 1)
            Vue.set(tpa, 'billing_start_period', uw._bulkMonth)
            Vue.set(tpa, '_committed_status', 1)
            Vue.set(tpa, 'editing', false)
            tpa._original = null
          }
        })
        uw._selectedIds = []
        var tableRef = this.$refs['table-' + uw.underwriter_id]
        if (tableRef && tableRef[0]) tableRef[0].clearSelection()
      } catch (err) {
        this.error = 'Bulk enable failed. Please try again.'
      } finally {
        Vue.set(uw, '_bulkSaving', false)
      }
    },

    bulkDisable(uw) {
      var enabledIds = uw.tpas
        .filter(function(t) { return uw._selectedIds.includes(t.id) && t._committed_status !== 0 })
        .map(function(t) { return t.id })
      if (enabledIds.length === 0) return
      this.disablePendingContext = { type: 'bulk', uw: uw, ids: enabledIds, count: enabledIds.length }
      this.showDisableModal = true
    },

    async _executeBulkDisable(uw, ids) {
      uw._bulkSaving = true
      try {
        await BillingManagementService.bulkUpdateBilling(uw.underwriter_id, {
          enable: false,
          updates: ids.map(function(id) { return { tpa_company_id: id } }),
        })
        uw.tpas.forEach(function(tpa) {
          if (ids.includes(tpa.id)) {
            Vue.set(tpa, 'billing_status', 0)
            Vue.set(tpa, 'billing_start_period', null)
            Vue.set(tpa, '_committed_status', 0)
            Vue.set(tpa, 'editing', false)
            tpa._original = null
          }
        })
        uw._selectedIds = uw._selectedIds.filter(function(id) { return !ids.includes(id) })
        var tableRef = this.$refs['table-' + uw.underwriter_id]
        if (tableRef && tableRef[0]) tableRef[0].clearSelection()
      } catch (err) {
        this.error = 'Bulk disable failed. Please try again.'
      } finally {
        uw._bulkSaving = false
      }
    },

    // ── Add TPA modal ─────────────────────────────────────

    openAddTpaModal(uw) {
      this.addTpaContext = { uw: uw }
      this.showAddTpaModal = true
    },

    async onAddTpaConfirm(payload) {
      // payload = { selectedIds: [tpa_company_id, ...], billingStartPeriod: 'YYYY-MM-DD' }
      var uw = this.addTpaContext ? this.addTpaContext.uw : null
      if (!uw) return

      try {
        var resp = await BillingManagementService.bulkUpdateBilling(uw.underwriter_id, {
          enable: payload.enable,
          billingStartPeriod: payload.billingStartPeriod,
          updates: payload.selectedIds.map(function(id) { return { tpa_company_id: id } }),
        })

        if (resp.data && resp.data.errors && resp.data.errors.length > 0) {
          this.error = 'Some TPAs could not be added. Please refresh and try again.'
        }

        // Add new TPA rows into the table in-place
        var self = this
        payload.selectedIds.forEach(function(tpaId) {
          var tpaInfo = uw.available_tpas.find(function(t) { return t.id === tpaId })
          if (!tpaInfo) return

          var newTpa = {
            id: tpaId,
            name: tpaInfo.name,
            billing_status: payload.enable ? 1 : 0,
            billing_start_period: payload.billingStartPeriod,
            _committed_status: payload.enable ? 1 : 0,
            editing: false,
            saving: false,
            _original: null,
            _switchKey: 0,
          }

          uw._allTpas.push(newTpa)
          uw.tpas.push(newTpa)

          // Remove from available_tpas so it won't appear again in modal
          var idx = uw.available_tpas.findIndex(function(t) { return t.id === tpaId })
          if (idx !== -1) uw.available_tpas.splice(idx, 1)
        })

        this.showAddTpaModal = false
        this.addTpaContext = null

        if (Vue.prototype.$toasted) {
          Vue.prototype.$toasted.show('TPA billing added successfully', { type: 'success', duration: 3000 })
        }
      } catch (err) {
        console.error('Failed to add TPA billing:', err)
        this.error = 'Failed to add TPA billing. Please try again.'
      }
    },

    // ── Badge helpers ─────────────────────────────────────

    getBillingStatus(uw) {
      var enabled = uw._allTpas.filter(function(t) { return t.billing_status }).length
      var total = uw._allTpas.length
      if (total === 0) return 'No TPAs'
      if (enabled === 0) return 'All Off'
      if (enabled === total) return 'All On'
      return enabled + '/' + total + ' On'
    },

    getBillingBadgeVariant(uw) {
      var enabled = uw._allTpas.filter(function(t) { return t.billing_status }).length
      var total = uw._allTpas.length
      if (total === 0 || enabled === 0) return 'off'
      if (enabled === total) return 'on'
      return 'partial'
    },
  }
}
</script>

<style scoped>
.billing-tab { font-size: 12px; }

.billing-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
  gap: 8px;
  flex-wrap: wrap;
}
.billing-select { font-size: 12px; min-width: 150px; }
.refresh-btn { font-size: 12px; }
.gap-2 { gap: 0.5rem; }

.billing-summary-bar {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 6px 12px;
  margin-bottom: 10px;
  font-size: 12px;
}

.billing-accordion { display: flex; flex-direction: column; gap: 5px; }

.uw-card {
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.uw-header {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 12px;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #fff;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;
}
.uw-header:hover { background: #f0fdf8; }

/* Grid, not flex. Flex negotiated widths between the name, the status badge and
   the billing-type controls, and the badge lost - it was crushed underneath them.
   A grid cannot do that: the columns are declared, so nothing overlaps and the
   label row above lines up because it uses the identical template. Column 1 is
   the only flexible one; every other column is a fixed width, because two
   separate grids do NOT share `auto` column sizing. */
.uw-header--bt,
.bt-header { grid-template-columns: minmax(0, 1fr) 210px 210px 132px 12px; }

.uw-header__main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;          /* lets the name truncate rather than push the grid */
}
.uw-header__left { display: flex; align-items: center; gap: 8px; min-width: 0; }
.uw-header__icon { color: #1c4a50; font-size: 13px; }
.uw-header__name {
  color: #1c4a50; font-size: 12px; font-weight: 600;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

.uw-header__chevron { color: #1c4a50; font-size: 11px; width: 12px; flex: none; }

.tpa-count-badge {
  background: rgba(28, 74, 80, 0.08);
  color: #1c4a50;
  font-size: 10px;
  padding: 1px 7px;
  border-radius: 20px;
  font-weight: 400;
}

.billing-status-badge {
  flex: none;
  white-space: nowrap;
  font-size: 10px;
  padding: 1px 8px;
  border-radius: 20px;
  font-weight: 500;
}
.billing-status-badge--on      { background: #2dce89; color: #fff; }
.billing-status-badge--off     { background: #f1f3f5; color: #6c757d; }
.billing-status-badge--partial { background: #fb6340; color: #fff; }

.uw-body { background: #fff; }
.uw-table-wrap { overflow-x: auto; }

.tpa-search-bar { background: #fafafa; border-bottom: 1px solid #f1f3f5; }
.tpa-search-input { font-size: 12px; max-width: 220px; }

/* Add TPA button */
.add-tpa-btn {
  font-size: 12px;
  color: #1c4a50 !important;
  font-weight: 500;
  text-decoration: none !important;
  white-space: nowrap;
}
.add-tpa-btn:hover { color: #153a3f !important; }

.uw-header--open {
  background: #f0fdf8;
}

.bulk-bar {
  background-color: #eaf6ff;
  border: 1px solid #b8daff;
  border-radius: 6px;
  gap: 8px;
}
.bulk-count { font-weight: 600; font-size: 0.8rem; color: #32325d; white-space: nowrap; }
.bulk-label { font-size: 0.75rem; color: #525f7f; white-space: nowrap; }

.uw-footer { border-top: 1px solid #f1f3f5; background: #fafafa; }

.uw-table-wrap >>> .el-table .el-table__row {
  height: 32px !important;
}
.uw-table-wrap >>> .el-table .el-table__header-row {
  height: 28px !important;
}

/* Compact el-table rows */
.uw-table-wrap >>> .el-table .el-table__row td {
  padding: 2px 0 !important;
}
.uw-table-wrap >>> .el-table .el-table__header th {
  padding: 2px 0 !important;
  font-size: 11px;
}
.bulk-bar .btn-sm{
  min-height: 25px !important;
}
.uw-table-wrap >>> .el-table .el-table__header .cell {
  min-height: 34px !important;
}
.uw-table-wrap >>> .el-table .cell {
  padding-left: 8px !important;
  padding-right: 8px !important;
  line-height: 1.3;
}

.row-saving  { opacity: 0.6; pointer-events: none; }
.row-editing { background-color: #f0fdf8 !important; }
.tpa-off { color: #adb5bd !important; }

/* Date picker */
.billing-date-picker { width: 150px; }
.billing-date-picker >>> .mx-input-wrapper { width: 150px; }
.billing-date-picker >>> .mx-input {
  height: 22px;
  font-size: 11px;
  padding: 1px 22px 1px 6px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  color: #495057;
  box-shadow: none;
}
.billing-date-picker >>> .mx-input:hover { border-color: #1c4a50; }
.billing-date-picker >>> .mx-input:focus {
  border-color: #1c4a50;
  box-shadow: 0 0 0 2px rgba(28, 74, 80, 0.12);
}
.billing-date-picker >>> .mx-input:disabled {
  background: #f8f9fa;
  color: #adb5bd;
  cursor: default;
}
.billing-date-picker >>> .mx-icon-calendar { font-size: 13px; color: #6c757d; }
.billing-date-picker >>> .mx-table-date .cell.active {
  background: #1c4a50 !important;
  border-radius: 4px;
}
.billing-date-picker >>> .mx-table-date .cell:not(.disabled):hover {
  background: #e8f4f0 !important;
  color: #1c4a50 !important;
}

.placeholder-date { color: #adb5bd; font-size: 11px; }

.row-actions { display: flex; gap: 4px; justify-content: flex-end; }
.row-actions >>> .btn-sm {
  padding: 0px 6px !important;
  font-size: 11px !important;
  line-height: 1.4 !important;
}

.empty-state {
  text-align: center;
  padding: 48px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.dm-root { position: relative; padding: 4px 0 0; }
.dm-close {
  position: absolute; top: -8px; right: -8px;
  display: flex; align-items: center; justify-content: center;
  width: 36px; height: 36px; border-radius: 50%; border: none;
  background-color: transparent; color: #8898aa; font-size: 1.4rem;
  cursor: pointer; transition: background-color 0.15s ease, color 0.15s ease;
  padding: 0; line-height: 1;
}
.dm-close:hover { background-color: #f0f0f0; color: #32325d; }
.dm-header { display: flex; align-items: flex-start; gap: 14px; margin-bottom: 20px; }
.dm-header__icon-wrap {
  display: flex; align-items: center; justify-content: center;
  width: 44px; height: 44px; border-radius: 50%; background-color: #fff3cd; flex-shrink: 0;
}
.dm-header__icon { font-size: 1.5rem; color: #e67e22; }
.dm-header__title { font-size: 1rem; font-weight: 700; color: #32325d; margin: 0 0 4px; }
.dm-header__sub { font-size: 0.88rem; color: #525f7f; margin: 0; }
.dm-info {
  background-color: #f8f9fa;
  border-left: 3px solid #1c4a50;
  border-radius: 0 6px 6px 0;
  padding: 12px 14px; margin-bottom: 24px;
}
.dm-info__label {
  font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.05em; color: #8898aa; margin-bottom: 10px;
}
.dm-info__row {
  display: flex; align-items: baseline; gap: 8px;
  font-size: 0.88rem; color: #495057; line-height: 1.5; margin-bottom: 6px;
}
.dm-info__row:last-child { margin-bottom: 0; }
.dm-info__check { color: #1c4a50; font-weight: 700; flex-shrink: 0; font-size: 0.9rem; }
.dm-actions { display: flex; justify-content: flex-end; gap: 8px; }

/* ---- Billing type: two switches per underwriter ------------------------- *
   .bt-header__col and .bt-cell share a width (switch + gap + the 150px picker), and both rows end with an 8px gap
   plus a fixed-width chevron slot, so the labels line up over the switches. The
   status badge varies in width but sits to the LEFT of the group, so it cannot
   shift it. */
.bt-group-header {
  display: grid;
  align-items: center;
  gap: 8px;
  padding: 0 14px 2px;   /* matches .uw-header's horizontal padding */
}
.bt-group-header__label {
  grid-column: 2 / 4;    /* spans the Market + LIMOSS columns */
  text-align: center;
  color: #1c4a50;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  padding-bottom: 4px;
  border-bottom: 1px solid #dee2e6;
}
.bt-header {
  display: grid;
  align-items: center;
  gap: 8px;
  padding: 0 14px 5px;   /* matches .uw-header's horizontal padding */
}
.bt-header__title {
  color: #1c4a50;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}
.bt-header__info { color: #8898aa; font-size: 11px; cursor: help; vertical-align: -1px; }
.bt-header__col {
  text-align: center;
  color: #8898aa;
  font-size: 11px;
  white-space: nowrap;
}

.bt-tip { text-align: left; font-size: 12px; line-height: 1.45; }
.bt-tip p { margin: 0 0 7px; }
.bt-tip p:last-child { margin-bottom: 0; }

.bt-toggle { width: 52px; flex: none; }   /* $custom-toggle-width */
/* Stops the mouse reaching the checkbox. Not sufficient on its own - it does
   not remove the input from the tab order - so the handler guards too. */
.bt-toggle--locked { pointer-events: none; opacity: 0.5; }
.bt-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
}
.bt-actions .btn { font-size: 11px; padding: 2px 9px; white-space: nowrap; }
.bt-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
}
</style>